import { Font } from '@react-pdf/renderer'

export const MAX_UNBROKEN_TOKEN_LENGTH = 12
const SEPARATOR_PATTERN = /([-/._])/

function chunk(token: string, size: number): string[] {
  const pieces: string[] = []
  for (let index = 0; index < token.length; index += size) {
    pieces.push(token.slice(index, index + size))
  }
  return pieces
}

/**
 * Long part numbers overflow react-pdf columns. Split at separators (kept
 * with the preceding piece) and otherwise into fixed chunks; never insert
 * zero-width spaces, which Helvetica renders as visible gaps.
 */
export function splitLongToken(
  word: string,
  maxLength: number = MAX_UNBROKEN_TOKEN_LENGTH
): string[] {
  if (word.length <= maxLength) return [word]
  const parts = word.split(SEPARATOR_PATTERN).filter((part) => part.length > 0)
  const merged: string[] = []
  for (const part of parts) {
    if (SEPARATOR_PATTERN.test(part) && merged.length > 0) {
      merged[merged.length - 1] += part
    } else {
      merged.push(part)
    }
  }
  return merged.flatMap((part) =>
    part.length > maxLength ? chunk(part, maxLength) : [part]
  )
}

/**
 * react-pdf's hyphenation callback is global. Install the artifact splitter
 * only for the duration of `render`, then put back whatever was there.
 */
export async function withArtifactPdfHyphenation<T>(
  render: () => Promise<T>
): Promise<T> {
  const previous = Font.getHyphenationCallback()
  Font.registerHyphenationCallback((word) => splitLongToken(word))
  try {
    return await render()
  } finally {
    Font.hyphenationCallback = previous
  }
}
